import { useCallback, useEffect, useMemo, useRef } from "react";
import { JD_STATUSES, isJdGenerationFailed, PENDING_JD_REFRESH_MS } from "constants/jobs.constants";

// Runs the latest `load` one call at a time. A normal reload asked for while one
// is running runs right after it; a quiet (silent) one is skipped.
export const useJdListReload = (load) => {
  const loadRef = useRef(load);
  loadRef.current = load;
  const runningRef = useRef(false);
  const queuedRef = useRef(false);

  const reload = useCallback(async (silent = false) => {
    if (runningRef.current) {
      if (!silent) queuedRef.current = true;
      return;
    }
    runningRef.current = true;
    try {
      await loadRef.current(silent);
    } finally {
      runningRef.current = false;
      if (queuedRef.current) {
        queuedRef.current = false;
        reload();
      }
    }
  }, []);

  return reload;
};

// While any JD is queued or generating, reload the list quietly so its status stays current.
export const usePendingJdRefresh = (jobs, reload) => {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const hasPendingJds = useMemo(
    () => (jobs || []).some((job) => job?.jd_status === JD_STATUSES.PENDING_ANALYSIS && !isJdGenerationFailed(job)),
    [jobs]
  );

  useEffect(() => {
    if (!hasPendingJds) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) reloadRef.current(true);
    }, PENDING_JD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [hasPendingJds]);
};
